import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import { MoreVertical } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { UserAvatar, AppBadge, ConfirmModal } from "../ui";
import { can } from "../../utils/access";

const STATUS_COLOR = {
  Ongoing: "amber",
  Completed: "emerald",
  Offered: "emerald",
  Joined: "emerald",
  Rejected: "rose",
  Withdrawn: "rose",
  Cancelled: "rose",
  Scheduled: "blue",
};

const InterviewListRow = ({ interview, onDelete, hideActions, isStudentView, readOnly, basePath = "/interviews" }) => {
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const {
    _id,
    studentId,
    companyName,
    interviewerName,
    currentStatus,
    rounds = [],
  } = interview;

  const roundsCompleted = rounds.filter(
    r => r.status !== "Scheduled" && r.status !== "Ongoing" && r.status !== "Rescheduled"
  ).length;
  const totalRounds = rounds.length;

  const nextRound = rounds.find(r => r.status === "Scheduled" || r.status === "Ongoing");

  return (
    <tr>
      {/* Student */}
      <td>
        <div className="iv-cell-user">
          <UserAvatar
            name={studentId?.name || "User"}
            src={studentId?.avatar && studentId.avatar.trim() ? studentId.avatar : undefined}
            size="sm"
          />
          <div
            className="iv-cell-user__info"
            onClick={() => !readOnly && navigate(`${basePath}/${_id}`)}
          >
            <span className="iv-cell-user__name">{studentId?.name}</span>
            <span className="iv-cell-user__sub">{studentId?.email}</span>
            <span className="iv-cell-user__sub">{studentId?.mobile}</span>
          </div>
        </div>
      </td>

      {/* Company */}
      <td>
        <span className="iv-cell-text">{companyName || "—"}</span>
      </td>

      {/* Next Round */}
      <td>
        {nextRound ? (
          <div>
            <div className="iv-cell-text">{nextRound.round || "Next Round"}</div>
            <div className="iv-cell-sub">
              {nextRound.date && !isNaN(new Date(nextRound.date))
                ? new Date(nextRound.date).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    timeZone: "Asia/Kolkata",
                  })
                : "N/A"}
            </div>
            {nextRound.time && <div className="iv-cell-sub">{nextRound.time}</div>}
          </div>
        ) : (
          <span className="iv-cell-sub">{isStudentView ? "No upcoming rounds" : "—"}</span>
        )}
      </td>

      {/* Interviewer */}
      <td>
        <span className="iv-cell-text">
          {interview.interviewerDisplayName || interviewerName?.name || "—"}
        </span>
      </td>

      {/* Status */}
      <td>
        <AppBadge color={STATUS_COLOR[currentStatus]} variant="default">
          {currentStatus} ({roundsCompleted}/{totalRounds})
        </AppBadge>
      </td>

      {/* Actions */}
      {!readOnly && (
        <td onClick={e => e.stopPropagation()}>
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="light"
              bsPrefix="p-0 border-0 bg-transparent shadow-none"
              className="iv-action-btn"
            >
              <MoreVertical size={16} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => navigate(`${basePath}/${_id}`)}>
                View Details
              </Dropdown.Item>
              {can("interviews.manage") && (
                <Dropdown.Item onClick={() => navigate(`${basePath}/edit/${_id}`)}>
                  Edit
                </Dropdown.Item>
              )}
              {onDelete && can("interviews.delete") && (
                <Dropdown.Item
                  className="text-danger"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </td>
      )}

      <ConfirmModal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={() => { setShowDeleteConfirm(false); onDelete(_id); }}
        title="Delete Interview"
        message="Are you sure you want to delete this interview?"
        confirmText="Delete"
        variant="danger"
      />
    </tr>
  );
};

export default InterviewListRow;
